import baseApiSlice from 'api/baseApiSlice';
import { toSortDirection } from 'api/remoteConfirmation/serializers';

import { toDonationDetail } from './donationSerializers';
import { DonationsTags } from './donationsTags';
import {
  DonationStatus,
  type ActiveDonationsResponse,
  type DonationDetail,
  type DonationHistoryResponse,
  type GetDonationHistoryParams,
} from './donationTypes';
import {
  resolveHistoryStatuses,
  toDonationHistoryFilters,
  type DonationHistoryFilters,
} from './historyStatusSerializers';

const donationsAPISlice = baseApiSlice
  .enhanceEndpoints({
    addTagTypes: Object.values(DonationsTags),
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getActiveDonations: build.query<ActiveDonationsResponse, void>({
        query: () => ({
          url: '/charity/donations/v1/active',
          method: 'GET',
        }),
        providesTags: [DonationsTags.ActiveDonations],
      }),
      getDonationDetail: build.query<DonationDetail, string>({
        query: (itemId) => ({
          url: `/charity/donations/v1/${itemId}`,
          method: 'GET',
        }),
        transformResponse: toDonationDetail,
        providesTags: (_result, _error, itemId) => [
          { type: DonationsTags.ActiveDonations, id: itemId },
        ],
      }),
      reserveDonation: build.mutation<void, string>({
        query: (itemId) => ({
          url: `/charity/donations/v1/${itemId}/reserve`,
          method: 'POST',
        }),
        async onQueryStarted(itemId, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              donationsAPISlice.util.updateQueryData('getActiveDonations', undefined, (draft) => {
                const donation = draft.donations.find((item) => item.itemId === itemId);
                if (donation) {
                  donation.status = DonationStatus.Reserved;
                }
              }),
            );
            dispatch(
              donationsAPISlice.util.updateQueryData('getDonationDetail', itemId, (draft) => {
                draft.charityItemState = DonationStatus.Reserved;
              }),
            );
          } catch {
            dispatch(donationsAPISlice.util.invalidateTags([DonationsTags.ActiveDonations]));
          }
        },
      }),
      declineDonation: build.mutation<void, string>({
        query: (itemId) => ({
          url: `/charity/donations/v1/${itemId}/reject`,
          method: 'POST',
        }),
        async onQueryStarted(itemId, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              donationsAPISlice.util.updateQueryData('getActiveDonations', undefined, (draft) => {
                draft.donations = draft.donations.filter((item) => item.itemId !== itemId);
              }),
            );
          } catch {
            dispatch(donationsAPISlice.util.invalidateTags([DonationsTags.ActiveDonations]));
          }
        },
      }),
      cancelReservation: build.mutation<void, { orderId: string; itemId: string }>({
        query: ({ orderId }) => ({
          url: `/charity/donations/v1/orders/${orderId}/cancel`,
          method: 'POST',
        }),
        async onQueryStarted({ itemId }, { dispatch, queryFulfilled }) {
          try {
            await queryFulfilled;
            dispatch(
              donationsAPISlice.util.updateQueryData('getActiveDonations', undefined, (draft) => {
                draft.donations = draft.donations.filter((donation) => donation.itemId !== itemId);
              }),
            );
            // A cancelled reservation becomes a history entry
            dispatch(donationsAPISlice.util.invalidateTags([DonationsTags.DonationHistory]));
          } catch {
            dispatch(donationsAPISlice.util.invalidateTags([DonationsTags.ActiveDonations]));
          }
        },
      }),
      getDonationHistory: build.query<DonationHistoryResponse, GetDonationHistoryParams>({
        query: ({ page, size, sort, storeFilter, statusFilter }) => {
          const statuses = resolveHistoryStatuses(statusFilter);
          return {
            url: '/charity/donations/v1/history',
            method: 'GET',
            params: {
              page,
              size,
              sortBy: sort.sortBy,
              sortDirection: toSortDirection(sort.direction),
              ...(storeFilter != null && storeFilter.length > 0
                ? { storeIds: storeFilter.join(',') }
                : {}),
              ...(statuses != null && statuses.length > 0 ? { statuses: statuses.join(',') } : {}),
            },
          };
        },
        providesTags: [DonationsTags.DonationHistory],
      }),
      getDonationHistoryFilters: build.query<DonationHistoryFilters, void>({
        query: () => ({
          url: '/charity/donations/v1/history/filters',
          method: 'GET',
        }),
        transformResponse: toDonationHistoryFilters,
        providesTags: [DonationsTags.DonationHistory],
      }),
    }),
  });

export default donationsAPISlice;
